import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, UNITS } from "@/lib/api";

const SAMPLE = "nome;qtd_embalagem;unidade;preco_embalagem;fornecedor\nPão brioche;6;un;10.80;Padaria Central\nCarne moída;1000;g;42.00;Frigorífico Bom Boi\nMaionese artesanal;700;g;10.00;";

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const sep = lines[0].includes(";") ? ";" : ",";
  const header = lines[0].toLowerCase().split(sep).map((h) => h.trim());
  const hasHeader = header.some((h) => ["nome", "name", "unidade", "unit", "preco", "preço", "price", "qtd_embalagem", "preco_embalagem"].includes(h));
  const rows = hasHeader ? lines.slice(1) : lines;
  const toNum = (s) => Number(String(s || "0").replace(/\./g, (m, i, str) => (str.includes(",") ? "" : m)).replace(",", ".")) || 0;
  return rows.map((l) => {
    const c = l.split(sep).map((x) => x.trim().replace(/^"|"$/g, ""));
    const unit = (c[2] || "un").toLowerCase().replace("gr", "g").replace("unidade", "un");
    return { name: c[0] || "", package_qty: toNum(c[1]) || 1, unit: UNITS.includes(unit) ? unit : "un", package_price: toNum(c[3]), supplier: c[4] || "" };
  }).filter((r) => r.name);
}

export const CsvImportDialog = ({ open, onOpenChange, onImported }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const rows = parseCsv(text);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result));
    reader.readAsText(file);
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([SAMPLE], { type: "text/csv" }));
    a.download = "modelo-insumos.csv";
    a.click();
  };
  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/ingredients/import", { rows });
      toast.success(`${r.data.imported} insumos importados`);
      onImported?.();
      onOpenChange(false);
      setText("");
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="csv-dialog">
        <DialogHeader><DialogTitle className="font-display">Importar insumos (CSV)</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Colunas: <span className="num">nome; qtd na embalagem; unidade (g, kg, ml, l, un...); preço da embalagem; fornecedor</span>. Você pode colar o conteúdo ou enviar o arquivo.</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={download} data-testid="csv-download-sample"><Download className="w-4 h-4 mr-2" />Baixar modelo</Button>
          <label className="inline-flex items-center gap-2 text-sm px-3 h-9 rounded-md border border-input cursor-pointer hover:bg-secondary">
            <Upload className="w-4 h-4" />Escolher arquivo<input type="file" accept=".csv,.txt" className="hidden" onChange={onFile} data-testid="csv-file-input" />
          </label>
        </div>
        <Textarea data-testid="csv-textarea" rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={SAMPLE} className="num text-xs" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground" data-testid="csv-preview-count">{rows.length} linha(s) reconhecida(s)</span>
          <Button data-testid="csv-import-btn" onClick={submit} disabled={!rows.length || loading}>{loading ? "Importando..." : `Importar ${rows.length}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
